import React, { useState, useEffect } from 'react';
import { FiMenu, FiX, FiEdit2, FiPlus, FiEye, FiTrash2, FiSettings } from 'react-icons/fi';
import api from '../../services/api';
import toast from 'react-hot-toast';
import ProductSelectorModal from './ProductSelectorModal';

const SectionManager = () => {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingSection, setEditingSection] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newSection, setNewSection] = useState({ 
    name: '', type: 'products', enabled: true, 
    productConfig: { selectionType: 'manual', productIds: [], limit: 12 },
    settings: { categoryIds: [], backgroundImage: '', button1Text: '', button1Link: '', button2Text: '', button2Link: '', viewAllCategoryId: '' }
  });
  const [showProductModal, setShowProductModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showFeaturesModal, setShowFeaturesModal] = useState(false);
  const [currentConfigSection, setCurrentConfigSection] = useState(null);
  const [allCategories, setAllCategories] = useState([]);
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    fetchSections();
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const cats = await api.get('/categories');
      setAllCategories(cats);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchSections = async () => {
    setLoading(true);
    try {
      const data = await api.get('/sections');
      setSections(data);
    } catch (err) {
      toast.error('Failed to load sections');
    } finally {
      setLoading(false);
    }
  };

  const handleDragStart = (e, index) => {
    e.dataTransfer.setData('text/plain', index);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = async (e, dropIndex) => {
    e.preventDefault();
    const dragIndex = parseInt(e.dataTransfer.getData('text/plain'));
    if (dragIndex === dropIndex) return;

    const reordered = [...sections];
    const draggedItem = reordered[dragIndex];
    reordered.splice(dragIndex, 1);
    reordered.splice(dropIndex, 0, draggedItem);

    const updated = reordered.map((item, idx) => ({ ...item, order: idx }));
    setSections(updated);

    try {
      const payload = updated.map(item => ({ id: item._id, order: item.order }));
      await api.post('/sections/reorder', { sections: payload });
      toast.success('Order updated');
    } catch (err) {
      toast.error('Failed to update order');
      fetchSections();
    }
  };

  const toggleEnabled = async (section) => {
    try {
      const updated = { ...section, enabled: !section.enabled };
      await api.put(`/sections/${section._id}`, updated);
      setSections(sections.map(s => s._id === section._id ? updated : s));
      toast.success(`${section.enabled ? 'Hidden' : 'Visible'} updated`);
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  const deleteSection = async (id) => {
    if (!window.confirm('Are you sure you want to delete this section?')) return;
    try {
      await api.delete(`/sections/${id}`);
      setSections(sections.filter(s => s._id !== id));
      toast.success('Section deleted');
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  const addSection = async () => {
    if (!newSection.name.trim()) {
      toast.error('Section name required');
      return;
    }
    try {
      const created = await api.post('/sections', newSection);
      setSections([...sections, created]);
      setShowAddModal(false);
      setNewSection({ name: '', type: 'products', enabled: true, productConfig: { selectionType: 'manual', productIds: [], limit: 12 }, settings: { categoryIds: [], backgroundImage: '', button1Text: '', button1Link: '', button2Text: '', button2Link: '', viewAllCategoryId: '' } });
      toast.success('Section added');
    } catch (err) {
      toast.error('Failed to add section');
    }
  };

  const saveEdit = async () => {
    if (!editingSection.name.trim()) return;
    try {
      await api.put(`/sections/${editingSection._id}`, editingSection);
      setSections(sections.map(s => s._id === editingSection._id ? editingSection : s));
      setEditingSection(null);
      toast.success('Section updated');
    } catch (err) {
      toast.error('Update failed');
    }
  };

  const openProductConfig = (section) => {
    setCurrentConfigSection(section);
    setShowProductModal(true);
  };

  const openCategoryConfig = (section) => {
    setCurrentConfigSection(section);
    setShowCategoryModal(true);
  };

  const openFeaturesConfig = (section) => {
    setCurrentConfigSection(section);
    setShowFeaturesModal(true);
  };

  const saveProductConfig = async (config) => {
    if (currentConfigSection) {
      const updated = { ...currentConfigSection, productConfig: config };
      try {
        await api.put(`/sections/${currentConfigSection._id}`, updated);
        setSections(sections.map(s => s._id === currentConfigSection._id ? updated : s));
        if (editingSection?._id === currentConfigSection._id) setEditingSection(updated);
        toast.success('Product configuration saved');
      } catch (err) {
        toast.error('Failed to save config');
      }
    } else if (newSection.type === 'products') {
      setNewSection({ ...newSection, productConfig: config });
    }
    setShowProductModal(false);
    setCurrentConfigSection(null);
  };

  const saveCategoryConfig = async (categoryIds) => {
    if (currentConfigSection) {
      const updated = { ...currentConfigSection, settings: { ...currentConfigSection.settings, categoryIds } };
      try {
        await api.put(`/sections/${currentConfigSection._id}`, updated);
        setSections(sections.map(s => s._id === currentConfigSection._id ? updated : s));
        if (editingSection?._id === currentConfigSection._id) setEditingSection(updated);
        toast.success('Category selection saved');
      } catch (err) {
        toast.error('Failed to save categories');
      }
    } else if (newSection.type === 'categories') {
      setNewSection({ ...newSection, settings: { ...newSection.settings, categoryIds } });
    }
    setShowCategoryModal(false);
    setCurrentConfigSection(null);
  };

  const saveFeaturesConfig = async (settings) => {
    if (currentConfigSection) {
      const updated = { ...currentConfigSection, settings: { ...currentConfigSection.settings, ...settings } };
      try {
        await api.put(`/sections/${currentConfigSection._id}`, updated);
        setSections(sections.map(s => s._id === currentConfigSection._id ? updated : s));
        if (editingSection?._id === currentConfigSection._id) setEditingSection(updated);
        toast.success('Features settings saved');
      } catch (err) {
        toast.error('Failed to save features');
      }
    } else if (newSection.type === 'features') {
      setNewSection({ ...newSection, settings: { ...newSection.settings, ...settings } });
    }
    setShowFeaturesModal(false);
    setCurrentConfigSection(null);
  };

  const uploadBackgroundImage = async (file, onSuccess) => {
    if (!file) return;
    setUploadingImage(true);
    const formData = new FormData();
    formData.append('image', file);
    try {
      const res = await api.post('/upload/hero', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (res.imageUrl) {
        onSuccess(res.imageUrl);
        toast.success('Image uploaded');
      } else {
        toast.error('Server did not return image URL');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Upload failed');
    } finally {
      setUploadingImage(false);
    }
  };

  const sectionTypes = [
    { value: 'hero', label: 'Hero Slider' },
    { value: 'products', label: 'Product Carousel' },
    { value: 'categories', label: 'Categories Carousel' },
    { value: 'features', label: 'Features Banner' },
    { value: 'grid', label: 'Image Grid' },
    { value: 'instagram', label: 'Instagram Feed' }
  ];

  if (loading) return <div className="text-center py-10">Loading sections...</div>;

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-gray-800">Manage Homepage Sections</h2>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 bg-[#d60167] text-white px-4 py-2 rounded-lg hover:bg-[#b0005a] transition"
        >
          <FiPlus /> Add Section
        </button>
      </div>

      <div className="space-y-2">
        {sections.map((section, idx) => (
          <div
            key={section._id}
            draggable
            onDragStart={(e) => handleDragStart(e, idx)}
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, idx)}
            className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg p-4 cursor-move hover:shadow-md transition"
          >
            <div className="flex items-center gap-4">
              <FiMenu className="text-gray-400 cursor-grab" size={20} />
              {editingSection?._id === section._id ? (
                <div className="flex flex-col gap-2">
                  <input
                    type="text"
                    value={editingSection.name}
                    onChange={(e) => setEditingSection({ ...editingSection, name: e.target.value })}
                    className="border border-gray-300 rounded px-2 py-1"
                    autoFocus
                  />
                  {section.type === 'products' && (
                    <select
                      value={editingSection.settings?.viewAllCategoryId || ''}
                      onChange={(e) => {
                        setEditingSection({
                          ...editingSection,
                          settings: { ...editingSection.settings, viewAllCategoryId: e.target.value }
                        });
                      }}
                      className="border border-gray-300 rounded px-2 py-1 text-sm"
                    >
                      <option value="">VIEW ALL goes to /shop (default)</option>
                      {allCategories.map(cat => (
                        <option key={cat._id} value={cat._id}>{cat.name}</option>
                      ))}
                    </select>
                  )}
                </div>
              ) : (
                <div>
                  <p className="font-medium text-gray-800">{section.name}</p>
                  <p className="text-xs text-gray-500">Type: {section.type}</p>
                  {section.type === 'products' && section.settings?.viewAllCategoryId && (
                    <p className="text-xs text-blue-600">
                      VIEW ALL → {allCategories.find(c => c._id === section.settings.viewAllCategoryId)?.name || 'Category'}
                    </p>
                  )}
                </div>
              )}
            </div>
            <div className="flex items-center gap-3">
              {section.type === 'products' && (
                <button
                  onClick={() => openProductConfig(section)}
                  className="text-purple-600 hover:text-purple-800"
                  title="Configure Products"
                >
                  <FiSettings size={18} />
                </button>
              )}
              {section.type === 'categories' && (
                <button
                  onClick={() => openCategoryConfig(section)}
                  className="text-green-600 hover:text-green-800"
                  title="Select Categories"
                >
                  <FiSettings size={18} />
                </button>
              )}
              {section.type === 'features' && (
                <button
                  onClick={() => openFeaturesConfig(section)}
                  className="text-blue-600 hover:text-blue-800"
                  title="Configure Features Banner"
                >
                  <FiSettings size={18} />
                </button>
              )}
              <button
                onClick={() => toggleEnabled(section)}
                className={`px-3 py-1 rounded text-sm ${section.enabled ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}
              >
                {section.enabled ? 'Visible' : 'Hidden'}
              </button>
              {editingSection?._id === section._id ? (
                <button onClick={saveEdit} className="text-green-600 hover:text-green-800">Save</button>
              ) : (
                <button onClick={() => setEditingSection(section)} className="text-blue-600 hover:text-blue-800">
                  <FiEdit2 size={18} />
                </button>
              )}
              <button onClick={() => deleteSection(section._id)} className="text-red-600 hover:text-red-800">
                <FiTrash2 size={18} />
              </button>
              <button className="text-gray-500 hover:text-gray-700">
                <FiEye size={18} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-sm">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Add New Section</h3>
              <button onClick={() => setShowAddModal(false)} className="text-gray-500 hover:text-gray-700">
                <FiX size={20} />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Section Name</label>
                <input
                  type="text"
                  value={newSection.name}
                  onChange={(e) => setNewSection({ ...newSection, name: e.target.value })}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  placeholder="e.g., Summer Collection"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Section Type</label>
                <select
                  value={newSection.type}
                  onChange={(e) => setNewSection({ ...newSection, type: e.target.value })}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                >
                  {sectionTypes.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>
              {newSection.type === 'products' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">VIEW ALL Category (Optional)</label>
                  <select
                    value={newSection.settings?.viewAllCategoryId || ''}
                    onChange={(e) => setNewSection({
                      ...newSection,
                      settings: { ...newSection.settings, viewAllCategoryId: e.target.value }
                    })}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  >
                    <option value="">Default (/shop)</option>
                    {allCategories.map(cat => (
                      <option key={cat._id} value={cat._id}>{cat.name}</option>
                    ))}
                  </select>
                </div>
              )}
              {newSection.type === 'products' && (
                <div>
                  <button
                    type="button"
                    onClick={() => openProductConfig(null)}
                    className="w-full border border-dashed border-purple-400 text-purple-600 py-2 rounded hover:bg-purple-50 text-sm"
                  >
                    Configure Products
                  </button>
                  {newSection.productConfig?.productIds?.length > 0 && (
                    <p className="text-xs text-gray-500 mt-1">{newSection.productConfig.productIds.length} products selected</p>
                  )}
                </div>
              )}
              {newSection.type === 'categories' && (
                <div>
                  <button
                    type="button"
                    onClick={() => openCategoryConfig(null)}
                    className="w-full border border-dashed border-green-400 text-green-600 py-2 rounded hover:bg-green-50 text-sm"
                  >
                    Select Categories
                  </button>
                  {newSection.settings?.categoryIds?.length > 0 && (
                    <p className="text-xs text-gray-500 mt-1">{newSection.settings.categoryIds.length} categories selected</p>
                  )}
                </div>
              )}
              {newSection.type === 'features' && (
                <div>
                  <button
                    type="button"
                    onClick={() => openFeaturesConfig(null)}
                    className="w-full border border-dashed border-blue-400 text-blue-600 py-2 rounded hover:bg-blue-50 text-sm"
                  >
                    Configure Banner
                  </button>
                </div>
              )}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="enabled"
                  checked={newSection.enabled}
                  onChange={(e) => setNewSection({ ...newSection, enabled: e.target.checked })}
                />
                <label htmlFor="enabled" className="text-sm text-gray-700">Enable section</label>
              </div>
              <button
                onClick={addSection}
                className="w-full bg-[#d60167] text-white py-2 rounded hover:bg-[#b0005a] transition text-sm font-medium"
              >
                Add Section
              </button>
            </div>
          </div>
        </div>
      )}

      {showProductModal && (
        <ProductSelectorModal
          initialConfig={currentConfigSection?.productConfig || newSection.productConfig || { selectionType: 'manual', productIds: [], limit: 12 }}
          onSave={saveProductConfig}
          onClose={() => {
            setShowProductModal(false);
            setCurrentConfigSection(null);
          }}
        />
      )}

      {showCategoryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-5 w-full max-w-md shadow-xl flex flex-col max-h-[90vh]">
            <div className="flex justify-between items-center mb-4 border-b pb-2">
              <h3 className="text-base font-semibold">Select Categories</h3>
              <button onClick={() => setShowCategoryModal(false)} className="text-gray-500 hover:text-gray-700">
                <FiX size={18} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto space-y-2 mb-4 pr-2 custom-scrollbar">
              {allCategories.map(cat => {
                const selected = currentConfigSection?.settings?.categoryIds?.includes(cat._id) ||
                                (currentConfigSection === null && newSection.settings?.categoryIds?.includes(cat._id));
                return (
                  <label key={cat._id} className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded border border-transparent hover:border-gray-200 cursor-pointer transition-colors">
                    <input
                      type="checkbox"
                      checked={selected || false}
                      onChange={(e) => {
                        let ids = currentConfigSection
                          ? [...(currentConfigSection.settings?.categoryIds || [])]
                          : [...(newSection.settings?.categoryIds || [])];
                        if (e.target.checked) {
                          ids.push(cat._id);
                        } else {
                          ids = ids.filter(id => id !== cat._id);
                        }
                        if (currentConfigSection) {
                          setCurrentConfigSection({
                            ...currentConfigSection,
                            settings: { ...currentConfigSection.settings, categoryIds: ids }
                          });
                        } else {
                          setNewSection({ ...newSection, settings: { ...newSection.settings, categoryIds: ids } });
                        }
                      }}
                      className="w-4 h-4 text-[#d60167]"
                    />
                    {cat.image && <img src={cat.image} alt="" className="w-8 h-8 object-cover rounded shadow-sm" />}
                    <span className="text-sm font-medium text-gray-700">{cat.name}</span>
                  </label>
                );
              })}
            </div>
            <div className="flex justify-end gap-2 pt-3 border-t">
              <button onClick={() => setShowCategoryModal(false)} className="px-4 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50">
                Cancel
              </button>
              <button
                onClick={() => {
                  const selectedIds = currentConfigSection
                    ? (currentConfigSection.settings?.categoryIds || [])
                    : (newSection.settings?.categoryIds || []);
                  saveCategoryConfig(selectedIds);
                }}
                className="px-4 py-1.5 text-sm bg-[#d60167] text-white rounded hover:bg-[#b0005a]"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {showFeaturesModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-lg shadow-xl flex flex-col max-h-[90vh]">
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-base font-semibold">Configure Features Banner</h3>
              <button onClick={() => setShowFeaturesModal(false)} className="text-gray-500 hover:text-gray-700">
                <FiX size={18} />
              </button>
            </div>
            <div className="p-4 space-y-4 overflow-y-auto">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Background Image</label>
                <div className="flex items-center gap-2">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        uploadBackgroundImage(file, (url) => {
                          if (currentConfigSection) {
                            setCurrentConfigSection({
                              ...currentConfigSection,
                              settings: { ...currentConfigSection.settings, backgroundImage: url }
                            });
                          } else {
                            setNewSection({ ...newSection, settings: { ...newSection.settings, backgroundImage: url } });
                          }
                        });
                      }
                    }}
                    disabled={uploadingImage}
                    className="border border-gray-300 text-sm p-1.5 rounded w-full"
                  />
                  {uploadingImage && <span className="text-xs text-gray-500 whitespace-nowrap">Uploading...</span>}
                </div>
                {(currentConfigSection?.settings?.backgroundImage || newSection.settings?.backgroundImage) && (
                  <div className="mt-2">
                    <img 
                      src={currentConfigSection?.settings?.backgroundImage || newSection.settings?.backgroundImage} 
                      alt="Background Preview" 
                      className="w-full h-24 object-cover rounded border border-gray-200"
                    />
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4 border-t border-gray-100 pt-4">
                <div>
                  <h4 className="font-medium text-sm mb-2 text-gray-800">Left Button</h4>
                  <label className="block text-xs text-gray-600 mb-1">Select Category</label>
                  <select
                    value=""
                    onChange={(e) => {
                      const selectedId = e.target.value;
                      if (!selectedId) return;
                      const selectedCat = allCategories.find(c => c._id === selectedId);
                      if (selectedCat) {
                        const button1Text = selectedCat.name;
                        const button1Link = `/category/${selectedCat.slug}`;
                        if (currentConfigSection) {
                          setCurrentConfigSection({
                            ...currentConfigSection,
                            settings: { ...currentConfigSection.settings, button1Text, button1Link }
                          });
                        } else {
                          setNewSection({ ...newSection, settings: { ...newSection.settings, button1Text, button1Link } });
                        }
                      }
                      e.target.value = '';
                    }}
                    className="w-full border border-gray-300 rounded text-sm px-2 py-1.5"
                  >
                    <option value="">-- Select Category --</option>
                    {allCategories.map(cat => (
                      <option key={cat._id} value={cat._id}>{cat.name}</option>
                    ))}
                  </select>
                  {(currentConfigSection?.settings?.button1Text || newSection.settings?.button1Text) && (
                    <div className="mt-2 p-2 bg-gray-50 rounded border border-gray-100">
                      <p className="text-xs text-gray-800 font-medium truncate">{currentConfigSection?.settings?.button1Text || newSection.settings?.button1Text}</p>
                      <p className="text-[10px] text-gray-500 truncate">{currentConfigSection?.settings?.button1Link || newSection.settings?.button1Link}</p>
                    </div>
                  )}
                </div>

                <div>
                  <h4 className="font-medium text-sm mb-2 text-gray-800">Right Button</h4>
                  <label className="block text-xs text-gray-600 mb-1">Select Category</label>
                  <select
                    value=""
                    onChange={(e) => {
                      const selectedId = e.target.value;
                      if (!selectedId) return;
                      const selectedCat = allCategories.find(c => c._id === selectedId);
                      if (selectedCat) {
                        const button2Text = selectedCat.name;
                        const button2Link = `/category/${selectedCat.slug}`;
                        if (currentConfigSection) {
                          setCurrentConfigSection({
                            ...currentConfigSection,
                            settings: { ...currentConfigSection.settings, button2Text, button2Link }
                          });
                        } else {
                          setNewSection({ ...newSection, settings: { ...newSection.settings, button2Text, button2Link } });
                        }
                      }
                      e.target.value = '';
                    }}
                    className="w-full border border-gray-300 rounded text-sm px-2 py-1.5"
                  >
                    <option value="">-- Select Category --</option>
                    {allCategories.map(cat => (
                      <option key={cat._id} value={cat._id}>{cat.name}</option>
                    ))}
                  </select>
                  {(currentConfigSection?.settings?.button2Text || newSection.settings?.button2Text) && (
                    <div className="mt-2 p-2 bg-gray-50 rounded border border-gray-100">
                      <p className="text-xs text-gray-800 font-medium truncate">{currentConfigSection?.settings?.button2Text || newSection.settings?.button2Text}</p>
                      <p className="text-[10px] text-gray-500 truncate">{currentConfigSection?.settings?.button2Link || newSection.settings?.button2Link}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="border-t p-4 flex justify-end gap-2 bg-gray-50 rounded-b-lg">
              <button onClick={() => setShowFeaturesModal(false)} className="px-4 py-1.5 text-sm border border-gray-300 bg-white rounded hover:bg-gray-100">
                Cancel
              </button>
              <button
                onClick={() => {
                  const settings = currentConfigSection
                    ? {
                        backgroundImage: currentConfigSection.settings?.backgroundImage || '',
                        button1Text: currentConfigSection.settings?.button1Text || '',
                        button1Link: currentConfigSection.settings?.button1Link || '',
                        button2Text: currentConfigSection.settings?.button2Text || '',
                        button2Link: currentConfigSection.settings?.button2Link || ''
                      }
                    : {
                        backgroundImage: newSection.settings?.backgroundImage || '',
                        button1Text: newSection.settings?.button1Text || '',
                        button1Link: newSection.settings?.button1Link || '',
                        button2Text: newSection.settings?.button2Text || '',
                        button2Link: newSection.settings?.button2Link || ''
                      };
                  saveFeaturesConfig(settings);
                }}
                className="px-4 py-1.5 text-sm bg-[#d60167] text-white rounded hover:bg-[#b0005a]"
              >
                Save Settings
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SectionManager;